import { Text } from '@thanx/uikit/text'
import { useGetRecoveryConfigQuery } from 'api/recoveryAi'
import Information from 'components/Icons/Information'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import type { Auth } from 'reducers/auth'
import WizardSimulator from 'scenes/RecoveryAI/Sandbox/WizardSimulator'
import { useStyletron } from 'styletron-react'
import { WIZARD_CONTENT_MAX_WIDTH } from '../constants'
import { TOUR2_STEPS } from '../tour/tour2Steps'
import WizardTour from '../tour/WizardTour'
import { useWizardState } from '../WizardContext'
import { useTourPlaybackHold } from './useTourPlaybackHold'

const t = buildTranslate('recovery_ai.wizard.testing')

// Gap between the decorative info icon and the app-version notice text,
// matching sandbox.standalone's app-version notice on the mirrored Agent
// Tuning tab.
const APP_VERSION_NOTICE_GAP = '8px'

type Props = {
  // Fired when the merchant completes the final guided persona (the simulator's
  // Complete CTA). index.tsx passes a useCallback-stable updater that sets the
  // session-only testingComplete flag, so this reference stays stable across
  // renders and never churns WizardSimulator/PlaybackNav (FE-1 D6
  // referential-stability contract).
  onComplete: () => void
}

// R12 onboarding Testing stage: embeds the merged pre-activation WizardSimulator
// (FE-1 / #3691) so a merchant can test their agent on the three guided personas
// before activation — the sandbox slot between config and activation.
//
// The embedded simulator owns the playback chrome (Previous/Next/Complete) AND
// its own loading + error region (SimulatorStatus) and never renders a
// context-less engine, so this stage does NOT re-implement load/error gating —
// it relies on the embedded shell. WizardSimulator is imported read-only (FE-1
// owns Sandbox/); it self-defaults useChatSource to useRecoveryChat.
const Testing = ({ onComplete }: Props) => {
  const [css] = useStyletron()
  const merchantId = useSelector(
    (state: { auth: Auth }) => state.auth.current_merchant_id || 0
  )
  // Flips when the merchant finishes the 3 guided personas and the simulator
  // enters its "Test your own" free phase — the trigger for the second tour and
  // the wizard's Continue unlock.
  const [freePhaseEntered, setFreePhaseEntered] = useState(false)
  // Stable wrapper around the simulator. Both tours scope their
  // re-measure/auto-start MutationObservers to this node instead of
  // document.body, so they only wake on the simulator's own DOM churn — the
  // persona-switch remount and AI-921's message-by-message reveal both happen
  // INSIDE this wrapper — rather than every mutation anywhere in the app. The
  // wrapper itself never remounts.
  const simulatorContainerRef = useRef<HTMLDivElement>(null)

  const handleAllScenariosComplete = useCallback(() => {
    setFreePhaseEntered(true)
    onComplete()
  }, [onComplete])

  // AI-255 tour show-policy. The tours are for merchants STILL setting up, so
  // gate on onboarding NOT being complete — a first-timer or a returning merchant
  // who cancelled mid-setup both see it, while a merchant who already finished
  // never does. (RTK dedupes this query with WizardSimulator's, so it's a cached
  // read.) The per-visit `tourSeen`/`tour2Seen` flags live in WizardContext
  // (provider-scoped): they stop a tour re-popping as the merchant moves between
  // steps within one visit, but reset when they leave /configure (Cancel) — so a
  // returning-unfinished merchant sees the tour again.
  const { data: config } = useGetRecoveryConfigQuery()
  const setupUnfinished = !config?.onboarding_completed
  const { state: wizardState, update } = useWizardState()

  // AI-921 sequencing: the guided scenario must not start opening itself until
  // tour 1 ends — the merchant presses its last step's "Got it", dismisses it,
  // or it never opens. Per-mount — WizardTour's startedRef is per-mount too, so
  // leaving the Testing step mid-tour and returning re-arms both together.
  const tour1Enabled =
    setupUnfinished && !freePhaseEntered && !wizardState.tourSeen
  // WizardTour stays dormant on a falsy merchantId while `enabled` stays true
  // forever — the one path that would freeze the phone permanently. Excluded
  // here rather than waiting on a tour that cannot open.
  const tour1Eligible = tour1Enabled && merchantId > 0
  const {
    hold: holdGuidedPlayback,
    handleTourStart,
    handleTourEnd,
  } = useTourPlaybackHold({ tourEligible: tour1Eligible })

  // Local end-latch beside the provider write: `update` is a no-op outside a
  // WizardProvider, so releasing on tourSeen alone would hold forever in a
  // provider-less mount.
  const handleTour1Seen = useCallback(() => {
    handleTourEnd()
    update({ tourSeen: true })
  }, [handleTourEnd, update])

  return (
    <div
      className={css({ maxWidth: WIZARD_CONTENT_MAX_WIDTH, margin: '0 auto' })}
    >
      {/* Header4 + BodyText4, matching the Agent Tuning tab's own intro — the two
          surfaces are meant to mirror each other, and the tab's sizes were already the
          Figma's. Header3/BodyText3 ran a step hot here (24px/16px vs 18px/14px).
            context: `tag="h3"` because uikit derives the tag from the VARIANT
            (text/Wrapper.js getTag: Header3 -> h3, Header4 -> h4), so taking the
            smaller type would otherwise have demoted this stage's title to h4 — the
            only wizard step not at h3, and level-equal to the tag="h4" subsections
            inside ReasoningChecklist / DecisionLogPanel that are supposed to sit
            UNDER it. Size and outline are decoupled deliberately. */}
      <div className="d-flex flex-column mb-l">
        <Text.Header4 tag="h3" bold>
          {t('title')}
        </Text.Header4>
        <Text.BodyText4 color="grey70" className="mt-xxs">
          {t('description')}
        </Text.BodyText4>
      </div>
      {/* App-version requirement for the RecoveryAI Experience, directly above
          the phone simulator — same subtle info-note pattern (and byte-identical
          copy) as the mirrored Agent Tuning tab (StandaloneSimulator). */}
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: APP_VERSION_NOTICE_GAP,
          marginBottom: '24px',
        })}
      >
        <span
          aria-hidden="true"
          className={css({ display: 'flex', flexShrink: 0 })}
        >
          <Information type="info" fillColor={palette.grey70} />
        </span>
        <Text.BodyText4 color="grey70">
          {t('app_version_notice')}
        </Text.BodyText4>
      </div>
      <div ref={simulatorContainerRef}>
        <WizardSimulator
          onAllScenariosComplete={handleAllScenariosComplete}
          // AI-921 sequencing: holds the guided phase's SCRIPTED transcript
          // until tour 1 ends. Flag off there is no transcript to hold.
          holdGuidedPlayback={holdGuidedPlayback}
          // Explicit write-through wiring: this is the one call site that owns the
          // WizardProvider, so the config-save mirror + free-phase testingComplete
          // re-arm route through here rather than WizardSimulator reaching for the
          // context itself (which would silently no-op outside a provider).
          onWizardPatch={update}
        />
      </div>
      {/* First tour — and now it DRIVES the guided phase rather than competing
          with it. The whole tour runs over a frozen opening; only finishing it
          (the last step's "Got it"), dismissing it, or it never opening starts
          the demo (Option A) — so nothing narrates a conversation already in
          motion. The phone shows the pinned greeting while held, so it is never
          empty under a step captioned "What your customer sees", and the guided
          script only starts sending once the hold lifts. Shown only while setup
          is unfinished, at most once per visit; disabled once the free phase
          begins. */}
      <WizardTour
        merchantId={merchantId}
        enabled={tour1Enabled}
        onStart={handleTourStart}
        onSeen={handleTour1Seen}
        observeRef={simulatorContainerRef}
      />
      {/* Second tour — over the "Test your own" free phase; runs on the live
          simulator (no re-key), with its final step anchored to Continue. Same
          show-policy (unfinished setup, once per visit). */}
      <WizardTour
        merchantId={merchantId}
        steps={TOUR2_STEPS}
        translateKey="recovery_ai.wizard.tour2"
        enabled={setupUnfinished && freePhaseEntered && !wizardState.tour2Seen}
        onSeen={() => update({ tour2Seen: true })}
        observeRef={simulatorContainerRef}
      />
    </div>
  )
}

export default Testing
